import {useEffect, useState, useMemo} from "react";
import {
    addInventoryToCart,
    getBuyer,
    getInvoice,
    getInvoiceLines,
    getReturnsForInvoice,
    insertInvoicePayment,
    insertInvoiceLineItems,
    getInvoicePayments
} from "../../api/Api";
import {BuyerInvoiceProfile} from "./BuyerInvoiceProfile";
import {useParams} from "react-router";
import {Button, Card, Row, Col, message} from 'antd';
import {InvoiceLineTable} from "./InvoiceLineTable";
import {TotalsCard} from "./TotalsCard";
import {ReturnTable} from "../returns/ReturnTable";
import {InvoiceStateWidget} from "./InvoiceStateWidget";
import {InvoicePaymentTable} from "../accounting/InvoicePaymentTable";
import {AddPaymentWidget} from "./AddPaymentWidget";
import {InvoiceStatusType} from "../../api/Constants";
import {InventorySearchWidget} from "./InventorySearchWidget";
import {openPdfPrintWindow} from "./invoiceUtils";


export const InvoiceView = ({refresh, onRefresh, onApiFail, props}) => {
    const [invoice, setInvoice] = useState(undefined);
    const [invoiceLines, setInvoiceLines] = useState([]);
    const [buyer, setBuyer] = useState(undefined);
    const [returns, setReturns] = useState([]);
    const [payments, setPayments] = useState([]);
    const params = useParams();
    const invoiceId = params.invoiceId;
    const [messageApi, contextHolder] = message.useMessage();

    useEffect(() => {
        if (invoiceId === undefined) {
            return;
        }
        getInvoice(invoiceId).then(result => {
            setInvoice(result);
            return getInvoiceLines(invoiceId);
        }).then(lines => {
            const result = lines.map(l => {
                return {
                    ...l,
                    key: l.id
                }
            });
            setInvoiceLines(result);
            return getReturnsForInvoice(invoiceId);
        }).then(returns => {
            const result = returns.map(r => {
                return {
                    ...r,
                    key: r.id
                }
            });
            setReturns(result);
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });

        getInvoicePayments(invoiceId).then(payments => {
            setPayments(payments.map(p => {
                return {
                    ...p,
                    key: p.id
                }
            }))
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        })
    }, [invoiceId, refresh, onApiFail]);

    useEffect(() => {
        if (invoice === undefined) return;
        getBuyer(invoice.buyerId).then(b => {
            setBuyer(b);
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
        });
    }, [invoice, refresh, onApiFail])

    const invoiceItems = useMemo(() => {
        if (!invoiceLines) { return undefined }
        const items = {}
        invoiceLines.forEach(l => {
            const {inventoryId} = l;
            l.key = inventoryId;
            items[inventoryId] = {
                item: l,
                quantity: l.quantity
            }
        })
        return items;
    }, [invoiceLines]);

    const addCartForWidget = (itemRecord, quantity) => {
        addInventoryToCart(itemRecord.id, quantity).then(r => {
            const newItems = {};
            newItems[itemRecord.id] = {
                quantity,
                item: {
                    id: itemRecord.id,
                    invoicePrice: 0,
                    linePrice: 0,
                    lineDiscount: 0,
                    discount: 0,
                },
            }
            console.log(`new item ${JSON.stringify(newItems)}`);
            return insertInvoiceLineItems(invoiceId, newItems);
        }).then(r => {
            if (onRefresh) onRefresh();
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
            if (onRefresh) onRefresh();
        })
    }

    const [isPaymentOpen, setIsPaymentOpen] = useState(false);

    const openPaymentModal = () => {
        setIsPaymentOpen(true);
    }

    const onPaymentOk = (amount, bankId, notes) => {
        const invoiceId = invoice.id;
        console.log(`inserting payment for invoice ${invoiceId} ${amount}`);
        insertInvoicePayment(invoiceId, amount, bankId, notes).then(r => {
            messageApi.open({
                type: 'success',
                content: 'Payment Added Successfully',
            });
            if (onRefresh) onRefresh();
        }).catch(e => {
            if ((e.status === 401) && (onApiFail)) {
                onApiFail();
            }
            messageApi.open({
                type: 'error',
                content: 'Add Payment Failed',
            });
        })
        setIsPaymentOpen(false);
    }

    const onPaymentCancel = () => {
        setIsPaymentOpen(false);
    }

    const onPrint = () => {
        console.log(`printing`);
        openPdfPrintWindow({
            invoice, buyer, invoiceLines, invoiceItems, payments, returns
        });
    }

    if (invoice === undefined) {
        return <div />
    }

    const enableEdit = (invoice.state === InvoiceStatusType.Created);
    const enablePayment = (invoice.state <= InvoiceStatusType.FullPayment);

    const remainingBalance = invoice.amountDue - invoice.amountPaid;

    const returnContent = (returns.length > 0) ?
        <Card title={"Returns"}><ReturnTable returns={returns} /></Card> : <div />

    const stateWidgetContent = <InvoiceStateWidget invoice={invoice} onApiFail={onApiFail} onRefresh={onRefresh} disabled={false} />

    const paymentTitleContent = <Row style={{width: '300px'}}>
        <Col span={8} style={{fontSize: '16px', margin: '4px'}}>Payments</Col>
        {enablePayment &&
        <Col><Button onClick={openPaymentModal}>Add Payment</Button></Col>
        }
    </Row>

    return <div style={{textAlign: 'left'}}>
        {contextHolder}
        <Card title={invoice.invoiceNumber}>
            <Row style={{marginTop: '5px', marginBottom: '5px'}}>{stateWidgetContent}</Row>
            <Row>
                <Button onClick={onPrint}>Print</Button>
            </Row>
        </Card>

        <Row><BuyerInvoiceProfile buyer={buyer} /></Row>
        <Card title={"Invoice Items"}>
            <InvoiceLineTable invoiceLines={invoiceLines} invoice={invoice} onApiFail={onApiFail} onRefresh={onRefresh} enableEdit={enableEdit}/>
            {enableEdit && <Row>
                <Col style={{marginTop: '8px', marginLeft: '2px'}}><b>New Items</b></Col>
            </Row>}
            {enableEdit &&
            <InventorySearchWidget refresh={refresh} onApiFail={onApiFail} onSelect={addCartForWidget} />
            }
        </Card>
        <Card title={paymentTitleContent}>
            {(payments.length > 0) &&
                <InvoicePaymentTable invoice={invoice} payments={payments} />
            }
        </Card>
        <TotalsCard invoiceItems={invoiceItems} payments={payments} />
        {returnContent}
        <AddPaymentWidget open={isPaymentOpen} remainingBalance={remainingBalance}
                          onOk={onPaymentOk} onCancel={onPaymentCancel}/>
    </div>
}